import { createSelector, Selector } from '@reduxjs/toolkit'
import { EntityType, MasterLegalAgreementType, OrganisationType, TradingRelationship, TradingRelationshipDetail } from 'api'
import { GlobalState } from 'store'
import { createAllEntitiesSelector, createEntityGetterSelector } from './entitySelectors'
import { getEnumValues } from 'model'
import { intersection, isNil } from 'lodash-es'
import { notNilFilter, ParametricSelector } from 'utils'

export const tradingRelationshipsGetterSelector: Selector<
  GlobalState,
  (tradingEntityId: string) => TradingRelationship[]
> = createSelector(createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP), tradingRelationships => (tradingEntityId: string) =>
  tradingRelationships.filter(
    tradingRelationship =>
      tradingRelationship.buyTradingEntity === tradingEntityId || tradingRelationship.sellTradingEntity === tradingEntityId
  )
)

export const tradingRelationshipDetailsByTradingEntityIdGetterSelector: Selector<
  GlobalState,
  (tradingEntityId: string, organisationType: OrganisationType) => TradingRelationshipDetail[]
> = createSelector(
  [
    createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createEntityGetterSelector(EntityType.ORGANISATION)
  ],
  (tradingRelationships, getTradingEntity, getOrganisation) => (tradingEntityId: string, organisationType: OrganisationType) =>
    tradingRelationships
      .filter(tradingRelationship =>
        organisationType === OrganisationType.BUY_SIDE
          ? tradingRelationship.buyTradingEntity === tradingEntityId
          : tradingRelationship.sellTradingEntity === tradingEntityId
      )
      .map<TradingRelationshipDetail>(tradingRelationship => {
        const buyTradingEntity = getTradingEntity(tradingRelationship.buyTradingEntity)
        const sellTradingEntity = getTradingEntity(tradingRelationship.sellTradingEntity)
        return {
          tradingRelationship: tradingRelationship,
          buyTradingEntity: getTradingEntity(tradingRelationship.buyTradingEntity),
          buySideOrg: isNil(buyTradingEntity) ? null : getOrganisation(buyTradingEntity.organisationId),
          sellSideOrg: isNil(sellTradingEntity) ? null : getOrganisation(sellTradingEntity.organisationId),
          sellTradingEntity: getTradingEntity(tradingRelationship.sellTradingEntity),
          organisationType
        }
      })
)

export const tradingRelationshipGetterSelector: Selector<
  GlobalState,
  (buyTradingEntityId: string, sellTradingEntityId: string) => TradingRelationship | null
> = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)],
  tradingRelationships => (buyTradingEntityId, sellTradingEntityId) =>
    tradingRelationships.find(
      tradingRelationship =>
        tradingRelationship.buyTradingEntity === buyTradingEntityId && tradingRelationship.sellTradingEntity === sellTradingEntityId
    ) ?? null
)

export const createTradingRelationshipMasterLegalAgreementsSelector = <TProps>(
  fundIdSelector: ParametricSelector<{}, TProps, string | null | undefined>,
  counterpartyIdsSelector: ParametricSelector<{}, TProps, string[] | null | undefined>
): ParametricSelector<GlobalState, TProps, MasterLegalAgreementType[]> =>
  createSelector(
    [tradingRelationshipGetterSelector, fundIdSelector, counterpartyIdsSelector],
    (getTradingRelationship, fundId, counterpartyIds) => {
      const enumValues = getEnumValues(MasterLegalAgreementType)
      if (!fundId || !counterpartyIds || counterpartyIds.length === 0) {
        return enumValues
      }

      if (counterpartyIds.length === 1) {
        const mlas = getTradingRelationship(fundId, counterpartyIds[0])?.masterLegalAgreements
        return mlas && mlas.length > 0 ? mlas : enumValues
      }

      const counterpartyRelationships = counterpartyIds.map(counterpartyId => getTradingRelationship(fundId, counterpartyId))

      if (counterpartyRelationships.every(relationship => relationship?.masterLegalAgreements.length === 0)) {
        return enumValues
      }

      return intersection(
        ...counterpartyIds.map(counterpartyId => getTradingRelationship(fundId, counterpartyId)?.masterLegalAgreements).filter(notNilFilter)
      )
    }
  )
